export const isValidReorderIndex = (
  index: number,
  listLength: number,
): boolean => index >= 0 && index < listLength;

export const parseOneBasedPosition = (inputValue: string): number | null => {
  const nextPosition = Number(inputValue);
  if (!Number.isInteger(nextPosition)) {
    return null;
  }

  return nextPosition - 1;
};

export const moveRowToIndex = <T>(
  rows: T[],
  fromIndex: number,
  toIndex: number,
): T[] => {
  const reorderedRows = [...rows];
  const [currentRow] = reorderedRows.splice(fromIndex, 1);
  reorderedRows.splice(toIndex, 0, currentRow);

  return reorderedRows;
};

export const getDropReorderedRows = <T>(
  rows: T[],
  dragIndex: number | null,
  toIndex: number,
): T[] | null => {
  if (
    dragIndex === null ||
    dragIndex === toIndex ||
    !isValidReorderIndex(dragIndex, rows.length) ||
    !isValidReorderIndex(toIndex, rows.length)
  ) {
    return null;
  }

  return moveRowToIndex(rows, dragIndex, toIndex);
};

export const getSwitchOrderResult = <T>(
  rows: T[],
  fromIndex: number,
  inputValue: string,
): { toIndex: number; reorderedRows: T[] } | null => {
  const toIndex = parseOneBasedPosition(inputValue);
  if (
    toIndex === null ||
    !isValidReorderIndex(toIndex, rows.length) ||
    toIndex === fromIndex
  ) {
    return null;
  }

  return {
    toIndex,
    reorderedRows: moveRowToIndex(rows, fromIndex, toIndex),
  };
};

type ReorderRows = Record<string, any>[];

type ReorderCallback = (
  fromIndex: number,
  toIndex: number,
  reorderedRows: ReorderRows,
) => void;

interface CreateReorderHandlersParams {
  immutableData: ReorderRows;
  dragIndex: number | null;
  onReorder?: ReorderCallback;
  clearDragState: () => void;
}

export const createReorderHandlers = ({
  immutableData,
  dragIndex,
  onReorder,
  clearDragState,
}: CreateReorderHandlersParams) => {
  const handleDrop = (toIndex: number) => {
    const reorderedRows = getDropReorderedRows(
      immutableData,
      dragIndex,
      toIndex,
    );
    if (!reorderedRows || dragIndex === null) {
      clearDragState();
      return;
    }

    onReorder?.(dragIndex, toIndex, reorderedRows);
    clearDragState();
  };

  const handleSwitchOrderByInput = (
    fromIndex: number,
    inputValue: string,
  ): number => {
    if (!onReorder) {
      return fromIndex + 1;
    }

    const reorderResult = getSwitchOrderResult(
      immutableData,
      fromIndex,
      inputValue,
    );
    if (!reorderResult) {
      return fromIndex + 1;
    }

    const { toIndex, reorderedRows } = reorderResult;
    onReorder(fromIndex, toIndex, reorderedRows);

    // Current row moves to target index.
    return toIndex + 1;
  };

  return {
    handleDrop,
    handleSwitchOrderByInput,
  };
};
